import { Link } from "react-router-dom";
import { Mail, Globe2 } from "lucide-react";
import { BRAND_ACRONYM, BRAND_NAME, BRAND_DOMAIN, BRAND_LOGO, CONTACT_EMAIL } from "../lib/brand";
import { useLang } from "../lib/i18n";

// Social profiles come from SAME_AS in seo.config.js (injected at build time); empty → no row.
const SOCIAL = JSON.parse(process.env.REACT_APP_SAME_AS || "[]");

const T = {
  el: {
    cols: [["Ομοσπονδία", [["Σχετικά", "/about"], ["Ακεραιότητα", "/integrity"], ["Επαλήθευση", "/verify"]]],
      ["Αγώνες", [["Κατατάξεις", "/rankings"], ["Διοργανώσεις", "/competitions"], ["Πάνθεον", "/hall-of-fame"]]],
      ["Μητρώο", [["Παίκτες", "/players"], ["Σύλλογοι", "/clubs"], ["Ομοσπονδίες", "/federations"]]]],
    tagline: "Επίσημο μητρώο παικτών, παγκόσμιες κατατάξεις και διοργανώσεις.",
    contact: "Επικοινωνία", domain: "Επίσημο domain:", note: "Μόνο Virtual Points — χωρίς στοιχηματισμό με πραγματικά χρήματα.", lang: "Γλώσσα",
  },
  en: {
    cols: [["Federation", [["About", "/about"], ["Integrity", "/integrity"], ["Verify", "/verify"]]],
      ["Compete", [["Rankings", "/rankings"], ["Competitions", "/competitions"], ["Hall of Fame", "/hall-of-fame"]]],
      ["Registry", [["Players", "/players"], ["Clubs", "/clubs"], ["Federations", "/federations"]]]],
    tagline: "Official player registry, world rankings and competitions.",
    contact: "Contact", domain: "Official domain:", note: "Virtual Points only — no real-money gambling.", lang: "Language",
  },
};

function LangSwitch() {
  const { lang, setLang } = useLang();
  return (
    <div role="group" aria-label={T[lang].lang} data-testid="footer-lang-switch" className="self-start sm:self-auto inline-flex rounded-full border border-[rgba(212,175,55,0.35)] p-0.5 text-[11px] font-mono font-bold">
      {[["el", "ΕΛ"], ["en", "EN"]].map(([k, label]) => (
        <button key={k} type="button" data-testid={`footer-lang-${k}`} aria-pressed={lang === k} onClick={() => setLang(k)}
          className={`px-3 py-1 rounded-full ${lang === k ? "bg-[#E5B842] text-black" : "text-slate-400 hover:text-[#E5B842]"}`}
          style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{label}</button>
      ))}
    </div>
  );
}

const linkCls = "text-sm text-slate-400 hover:text-[#E5B842]";
const hostOf = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };

export function Footer() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <footer data-testid="site-footer" className="mt-16 border-t border-[rgba(212,175,55,0.22)] bg-[#000000]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1.2fr]">
        <div>
          <Link to="/" data-testid="footer-logo"><img src={BRAND_LOGO} alt={`${BRAND_ACRONYM} logo`} className="h-8 w-auto" /></Link>
          <p className="text-sm text-slate-400 mt-4 max-w-xs">{BRAND_NAME} — {t.tagline}</p>
        </div>
        {t.cols.map(([title, links]) => (
          <nav key={title} aria-label={title}>
            <div className="text-[11px] font-mono font-bold uppercase tracking-[0.25em] text-[#E5B842] mb-3">{title}</div>
            <ul className="space-y-2">
              {links.map(([label, to]) => (
                <li key={to}><Link to={to} data-testid={`footer-link-${to.slice(1)}`} className={linkCls} style={{ transition: "color 0.15s ease" }}>{label}</Link></li>
              ))}
            </ul>
          </nav>
        ))}
        <div>
          <div className="text-[11px] font-mono font-bold uppercase tracking-[0.25em] text-[#E5B842] mb-3">{t.contact}</div>
          <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-contact-email" className={`${linkCls} inline-flex items-center gap-2 break-all`} style={{ transition: "color 0.15s ease" }}>
            <Mail className="w-4 h-4 shrink-0" /> {CONTACT_EMAIL}
          </a>
          <div data-testid="footer-official-domain" className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-mono uppercase tracking-widest text-slate-500">
            <Globe2 className="w-4 h-4 text-[#E5B842]" /> <span className="whitespace-nowrap">{t.domain}</span> <span className="text-[#E5B842] normal-case">{BRAND_DOMAIN}</span>
          </div>
          {SOCIAL.length > 0 && (
            <div data-testid="footer-social" className="flex flex-wrap gap-3 mt-4">
              {SOCIAL.map((u) => <a key={u} href={u} target="_blank" rel="noopener noreferrer me" className={linkCls}>{hostOf(u)}</a>)}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-[rgba(212,175,55,0.12)]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <div data-testid="footer-copyright" className="text-xs text-slate-500">© 2026 {BRAND_NAME}. {t.note}</div>
          <LangSwitch />
        </div>
      </div>
    </footer>
  );
}
